<script>
import { mapActions, mapState } from 'vuex';
import {
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined,
  KeyOutlined,
  CopyOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons-vue';
import dayjs from 'dayjs';
import bem from '@/utils/bem';
import PageHeader from '@/components/ux/pageHeader/page-header.vue';

const b = bem('analytics-api-keys-page');

export default {
  components: {
    PageHeader,
    ReloadOutlined,
    PlusOutlined,
    DeleteOutlined,
    KeyOutlined,
    CopyOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    WarningOutlined,
    CloseCircleOutlined,
  },
  data() {
    return {
      title: 'Analytics API Keys',
      generateModalVisible: false,
      extendModalVisible: false,
      newKeyModalVisible: false,
      selectedKeyForExtend: null,
      generateForm: {
        companyId: null,
        expiresInDays: 365,
      },
      extendForm: {
        expiresInDays: 365,
      },
      newGeneratedKey: null,
      companies: [],
      companiesLoading: false,
      columns: [
        {
          title: 'Company',
          dataIndex: 'companyName',
          key: 'companyName',
          width: 200,
        },
        {
          title: 'API Key',
          dataIndex: 'apiKeyMasked',
          key: 'apiKeyMasked',
          width: 250,
        },
        {
          title: 'Status',
          key: 'status',
          width: 120,
        },
        {
          title: 'Expires',
          dataIndex: 'expiresAt',
          key: 'expiresAt',
          width: 180,
        },
        {
          title: 'Days Left',
          key: 'daysLeft',
          width: 120,
        },
        {
          title: 'Created',
          dataIndex: 'createdAt',
          key: 'createdAt',
          width: 180,
        },
        {
          title: 'Actions',
          key: 'actions',
          width: 150,
        },
      ],
      expiresInDaysOptions: [
        { value: 30, label: '30 days' },
        { value: 90, label: '90 days' },
        { value: 180, label: '180 days' },
        { value: 365, label: '1 year' },
        { value: 730, label: '2 years' },
      ],
    };
  },
  computed: {
    ...mapState({
      apiKeys: (state) => state.analyticsApiKeys.apiKeys,
      apiKeysLoading: (state) => state.analyticsApiKeys.apiKeysLoading,
      creatingApiKey: (state) => state.analyticsApiKeys.creatingApiKey,
      extendingApiKey: (state) => state.analyticsApiKeys.extendingApiKey,
      revokingApiKey: (state) => state.analyticsApiKeys.revokingApiKey,
      newApiKey: (state) => state.analyticsApiKeys.newApiKey,
    }),
  },
  methods: {
    b,
    ...mapActions('analyticsApiKeys', [
      'getApiKeys',
      'createApiKey',
      'extendApiKey',
      'revokeApiKey',
    ]),
    formatDate(date) {
      if (!date) return '-';
      return dayjs(date).format('MMM DD, YYYY HH:mm');
    },
    getDaysLeft(expiresAt) {
      if (!expiresAt) return null;
      const diff = dayjs(expiresAt).diff(dayjs(), 'day');
      return diff;
    },
    getStatusColor(expiresAt) {
      const daysLeft = this.getDaysLeft(expiresAt);
      if (daysLeft === null) return 'default';
      if (daysLeft < 0) return 'red';
      if (daysLeft <= 7) return 'red';
      if (daysLeft <= 30) return 'orange';
      return 'green';
    },
    getStatusText(expiresAt) {
      const daysLeft = this.getDaysLeft(expiresAt);
      if (daysLeft === null) return 'Unknown';
      if (daysLeft < 0) return 'Expired';
      if (daysLeft <= 7) return 'Expiring Soon';
      if (daysLeft <= 30) return 'Warning';
      return 'Active';
    },
    getStatusIcon(expiresAt) {
      const daysLeft = this.getDaysLeft(expiresAt);
      if (daysLeft === null) return 'close-circle-outlined';
      if (daysLeft < 0) return 'close-circle-outlined';
      if (daysLeft <= 7) return 'warning-outlined';
      if (daysLeft <= 30) return 'warning-outlined';
      return 'check-circle-outlined';
    },
    loadApiKeys() {
      this.getApiKeys();
    },
    async loadCompanies() {
      this.companiesLoading = true;
      try {
        const response = await this.$store.dispatch('companies/getCompanies');
        this.companies = response?.data || [];
      } catch (e) {
        console.error('Failed to load companies:', e);
      } finally {
        this.companiesLoading = false;
      }
    },
    openGenerateModal() {
      this.generateForm = {
        companyId: null,
        expiresInDays: 365,
      };
      this.loadCompanies();
      this.generateModalVisible = true;
    },
    closeGenerateModal() {
      this.generateModalVisible = false;
    },
    async handleGenerate() {
      try {
        const result = await this.createApiKey(this.generateForm);
        this.closeGenerateModal();
        if (result?.apiKey) {
          this.newGeneratedKey = result.apiKey;
          this.newKeyModalVisible = true;
        }
      } catch (e) {
        // Error handled in action
      }
    },
    openExtendModal(record) {
      this.selectedKeyForExtend = record;
      this.extendForm = {
        expiresInDays: 365,
      };
      this.extendModalVisible = true;
    },
    closeExtendModal() {
      this.extendModalVisible = false;
      this.selectedKeyForExtend = null;
    },
    async handleExtend() {
      try {
        await this.extendApiKey({
          id: this.selectedKeyForExtend.id,
          expiresInDays: this.extendForm.expiresInDays,
        });
        this.closeExtendModal();
      } catch (e) {
        // Error handled in action
      }
    },
    async handleRevoke(record) {
      try {
        await this.revokeApiKey(record.id);
      } catch (e) {
        // Error handled in action
      }
    },
    closeNewKeyModal() {
      this.newKeyModalVisible = false;
      this.newGeneratedKey = null;
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text);
        this.$message.success('API key copied to clipboard');
      } catch (e) {
        this.$message.error('Failed to copy to clipboard');
      }
    },
    filterCompanyOption(input, option) {
      return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
    },
  },
  mounted() {
    this.loadApiKeys();
  },
};
</script>

<template>
  <div :class="b()">
    <page-header :title="title">
      <template #actions>
        <a-space>
          <a-button type="primary" @click="openGenerateModal">
            <plus-outlined /> Generate Key
          </a-button>
          <a-button @click="loadApiKeys">
            <reload-outlined /> Refresh
          </a-button>
        </a-space>
      </template>
    </page-header>

    <a-card>
      <a-alert
        message="API Key Management"
        :description="'Generate and manage API keys for companies to access analytics tracking '
          + 'endpoints. Each company can have only one active key. Generating a new key will '
          + 'replace the existing one.'"
        type="info"
        show-icon
        style="margin-bottom: 16px"
      />

      <a-table
        :dataSource="apiKeys"
        :columns="columns"
        :loading="apiKeysLoading"
        row-key="id"
        :pagination="false"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'companyName'">
            <div :class="b('company')">
              <key-outlined :class="b('company-icon')" />
              <span>{{ record.companyName || `Company #${record.companyId}` }}</span>
            </div>
          </template>

          <template v-else-if="column.key === 'apiKeyMasked'">
            <code :class="b('api-key')">{{ record.apiKeyMasked }}</code>
          </template>

          <template v-else-if="column.key === 'status'">
            <a-tag :color="getStatusColor(record.expiresAt)">
              <check-circle-outlined v-if="getDaysLeft(record.expiresAt) > 30" />
              <warning-outlined v-else-if="getDaysLeft(record.expiresAt) > 0" />
              <close-circle-outlined v-else />
              {{ getStatusText(record.expiresAt) }}
            </a-tag>
          </template>

          <template v-else-if="column.key === 'expiresAt'">
            {{ formatDate(record.expiresAt) }}
          </template>

          <template v-else-if="column.key === 'daysLeft'">
            <span
              :class="[
                b('days-left'),
                { expired: getDaysLeft(record.expiresAt) < 0 },
                { warning: getDaysLeft(record.expiresAt) > 0
                  && getDaysLeft(record.expiresAt) <= 30 },
              ]"
            >
              <template v-if="getDaysLeft(record.expiresAt) < 0">
                Expired {{ Math.abs(getDaysLeft(record.expiresAt)) }} days ago
              </template>
              <template v-else>
                {{ getDaysLeft(record.expiresAt) }} days
              </template>
            </span>
          </template>

          <template v-else-if="column.key === 'createdAt'">
            {{ formatDate(record.createdAt) }}
          </template>

          <template v-else-if="column.key === 'actions'">
            <a-space>
              <a-tooltip title="Extend">
                <a-button type="link" size="small" @click="openExtendModal(record)">
                  <clock-circle-outlined /> Extend
                </a-button>
              </a-tooltip>
              <a-popconfirm
                title="Are you sure you want to revoke this API key? This action cannot be undone."
                ok-text="Yes, Revoke"
                cancel-text="Cancel"
                @confirm="handleRevoke(record)"
              >
                <a-tooltip title="Revoke">
                  <a-button type="link" size="small" danger :loading="revokingApiKey">
                    <delete-outlined /> Revoke
                  </a-button>
                </a-tooltip>
              </a-popconfirm>
            </a-space>
          </template>
        </template>
      </a-table>

      <a-empty
        v-if="!apiKeysLoading && !apiKeys.length"
        description="No API keys generated yet"
      >
        <a-button type="primary" @click="openGenerateModal">
          <plus-outlined /> Generate First Key
        </a-button>
      </a-empty>
    </a-card>

    <!-- Generate Key Modal -->
    <a-modal
      v-model:visible="generateModalVisible"
      title="Generate API Key"
      :confirm-loading="creatingApiKey"
      @ok="handleGenerate"
      @cancel="closeGenerateModal"
    >
      <a-form layout="vertical">
        <a-form-item label="Company" required>
          <a-select
            v-model:value="generateForm.companyId"
            placeholder="Select a company"
            show-search
            :filter-option="filterCompanyOption"
            :loading="companiesLoading"
            style="width: 100%"
          >
            <a-select-option
              v-for="company in companies"
              :key="company.id"
              :value="company.id"
              :label="company.name"
            >
              {{ company.name }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-form-item label="Expires In">
          <a-select
            v-model:value="generateForm.expiresInDays"
            style="width: 100%"
          >
            <a-select-option
              v-for="opt in expiresInDaysOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </a-select-option>
          </a-select>
        </a-form-item>

        <a-alert
          message="Warning"
          :description="'If the selected company already has an API key, '
            + 'it will be replaced with a new one.'"
          type="warning"
          show-icon
        />
      </a-form>
    </a-modal>

    <!-- Extend Key Modal -->
    <a-modal
      v-model:visible="extendModalVisible"
      title="Extend API Key"
      :confirm-loading="extendingApiKey"
      @ok="handleExtend"
      @cancel="closeExtendModal"
    >
      <a-form layout="vertical">
        <a-form-item label="Company">
          <a-input
            :value="selectedKeyForExtend?.companyName
              || `Company #${selectedKeyForExtend?.companyId}`"
            disabled
          />
        </a-form-item>

        <a-form-item label="Current Expiration">
          <a-input
            :value="formatDate(selectedKeyForExtend?.expiresAt)"
            disabled
          />
        </a-form-item>

        <a-form-item label="Extend By">
          <a-select
            v-model:value="extendForm.expiresInDays"
            style="width: 100%"
          >
            <a-select-option
              v-for="opt in expiresInDaysOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </a-select-option>
          </a-select>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- New Key Generated Modal -->
    <a-modal
      v-model:visible="newKeyModalVisible"
      title="API Key Generated"
      :footer="null"
      @cancel="closeNewKeyModal"
    >
      <a-alert
        message="Important: Copy this API key now!"
        :description="'This is the only time you will see the full API key. '
          + 'Make sure to copy and store it securely.'"
        type="warning"
        show-icon
        style="margin-bottom: 16px"
      />

      <div :class="b('new-key-container')">
        <code :class="b('new-key')">{{ newGeneratedKey }}</code>
        <a-button type="primary" @click="copyToClipboard(newGeneratedKey)">
          <copy-outlined /> Copy
        </a-button>
      </div>

      <div style="margin-top: 16px; text-align: right">
        <a-button @click="closeNewKeyModal">Close</a-button>
      </div>
    </a-modal>
  </div>
</template>

<style lang="scss">
.analytics-api-keys-page {
  padding: 24px;

  &__company {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__company-icon {
    color: #1890ff;
  }

  &__api-key {
    background: #f5f5f5;
    padding: 4px 8px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 13px;
  }

  &__days-left {
    font-weight: 500;

    &.expired {
      color: #ff4d4f;
    }

    &.warning {
      color: #faad14;
    }
  }

  &__new-key-container {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    background: #f5f5f5;
    border-radius: 8px;
  }

  &__new-key {
    flex: 1;
    font-family: monospace;
    font-size: 14px;
    word-break: break-all;
  }
}
</style>
